import { createSelector } from 'reselect';
import type { RootState } from '@/store';
import { roundToPlaces } from '@ianesthesie-kit/core';
import {
  CcamModifier,
  ConventionStatus,
  InsurancePlan,
  ManualFee
} from '@/modules/billing/billing.model';
import {
  InvoiceBuilderState,
  InvoiceCalculationType
} from '@/modules/billing/billing.slice';
import { BillingService } from './billing.service';
import { roundToCents } from './invoice.utils';

export const selectOptam = (state: RootState) => state.invoiceBuilder.optam;

export const selectBillingDoctor = (state: RootState) =>
  state.invoiceBuilder.billingDoctor;

export const selectPickedBillingDoctorId = (state: RootState) =>
  state.invoiceBuilder.pickedBillingDoctorId;

export const selectInvoiceBuilderTarget = (state: RootState) =>
  state.invoiceBuilder.target;

export const selectProcedureReimbursementInfosCount = (state: RootState) =>
  state.invoiceBuilder.procedureReimbursements?.length ?? 0;

/** CCAM and manual fee lines together, the count `MAX_BILLING_LINES` caps. */
export const selectBillingLineCount = (state: RootState) =>
  state.invoiceBuilder.procedureReimbursements.length +
  state.invoiceBuilder.manualFees.length;

export const selectProcedureReimbursementInfoAtIndex =
  (index: number) => (state: RootState) =>
    state.invoiceBuilder.procedureReimbursements?.[index]?.info;

export const selectProcedureReimbursementIds = (state: RootState): number[] =>
  state.invoiceBuilder.procedureReimbursements.map((pr) => pr.id);

export const selectSelectedProcedureReimbursementInfoCodes = (
  state: RootState
) =>
  state.invoiceBuilder.procedureReimbursements
    .map((pr) => pr.info?.code)
    .filter(Boolean) as string[];

export const selectSelectedProcedureReimbursementInfoCodesWithNames =
  createSelector(
    (state: RootState) => state.invoiceBuilder.procedureReimbursements,
    (reimbursements) =>
      reimbursements
        .filter((pr) => pr.info?.code)
        .map((pr) => ({ code: pr.info!.code, name: pr.info!.name }))
  );

export const selectProcedureReimbursementBaseForIndex =
  (index: number) => (state: RootState) => {
    const optam = state.invoiceBuilder.optam;
    const { info, forceEligibleBrssRateToFull } =
      state.invoiceBuilder.procedureReimbursements[index] ?? {};
    const eligibleBrssRate = forceEligibleBrssRateToFull
      ? 1
      : BillingService.getEligibleBrssRateForIndex(index);
    if (info && typeof optam === 'boolean') {
      const anesthesia =
        info[optam ? 'anesthesiaWithOptam' : 'anesthesiaWithoutOptam'];
      const surgeon =
        info[optam ? 'operatorWithOptam' : 'operatorWithoutOptam'];
      let ratio: number | undefined = undefined;
      if (anesthesia && surgeon) {
        ratio = anesthesia / surgeon;
      }

      return {
        anesthesia: anesthesia && anesthesia * eligibleBrssRate,
        surgeon: surgeon && surgeon * eligibleBrssRate,
        fixedConsultationFee: state.invoiceBuilder.fixedConsultationFee,
        ratio,
        eligibleBrssRate
      };
    }
    return { eligibleBrssRate };
  };

export const selectProcedureReimbursementCodesToEligibleRatesMap = (
  state: RootState
) =>
  state.invoiceBuilder.procedureReimbursements.reduce(
    (acc, { info, forceEligibleBrssRateToFull }, i) => {
      if (info) {
        acc[info.code] = forceEligibleBrssRateToFull
          ? 1
          : BillingService.getEligibleBrssRateForIndex(i);
      }
      return acc;
    },
    {} as Record<string, number>
  );

export const selectAnesthesiaHospitalCostForIndex =
  (index: number) => (state: RootState) =>
    state.invoiceBuilder.procedureReimbursements[index]
      ?.hospitalAnesthesiaPrice;

export const selectProcedureReimbursementBaseRatioForIndex =
  (index: number) => (state: RootState) =>
    selectProcedureReimbursementBaseForIndex(index)(state).ratio;

export const selectInsurancePlan = (state: RootState) =>
  state.invoiceBuilder.insurancePlan.plan;

export const selectInsurancePlanInputType = (state: RootState) =>
  state.invoiceBuilder.insurancePlan.planInput;

export const selectInsuranceManualPlan = (state: RootState) =>
  state.invoiceBuilder.insurancePlan.manualPlan;

export const selectInsurancePlanOptamValues = (
  state: RootState
):
  | InsurancePlan
  | InvoiceBuilderState['insurancePlan']['manualPlan']
  | undefined =>
  state.invoiceBuilder.insurancePlan.planInput === 'manualInput'
    ? state.invoiceBuilder.insurancePlan.manualPlan
    : state.invoiceBuilder.insurancePlan.plan;

const selectProcedureReimbursementBaseSums = (state: RootState) => {
  const count = selectProcedureReimbursementInfosCount(state);
  return Array.from({ length: count })
    .map((_, i) => selectProcedureReimbursementBaseForIndex(i)(state))
    .reduce(
      (acc, { anesthesia, surgeon, fixedConsultationFee }) => {
        acc.anesthesia += anesthesia ?? 0;
        acc.surgeon += surgeon ?? 0;
        acc.fixedConsultationFee += fixedConsultationFee ?? 0;
        return acc;
      },
      {
        anesthesia: 0,
        surgeon: 0,
        fixedConsultationFee: 0
      }
    );
};

export const selectInsuranceCoverage = createSelector(
  selectInsurancePlanOptamValues,
  selectOptam,
  selectProcedureReimbursementBaseSums,
  (plan, optam, base) => {
    if (
      !base.anesthesia ||
      !base.surgeon ||
      !plan ||
      typeof optam !== 'boolean'
    ) {
      return {};
    }

    if (
      !('id' in plan) &&
      (!plan.consultationWithOptam ||
        !plan.consultationWithOptam ||
        !plan.hospitalizationWithOptam ||
        !plan.hospitalizationWithoutOptam)
    ) {
      return {};
    }

    const consultPercentage =
      plan[optam ? 'consultationWithOptam' : 'consultationWithoutOptam'];
    const hospitPercentage =
      plan[optam ? 'hospitalizationWithOptam' : 'hospitalizationWithoutOptam'];
    const consultation = safeMult(consultPercentage, base.fixedConsultationFee);
    const anesthesia = safeMult(hospitPercentage, base.anesthesia);
    const procedure = safeMult(hospitPercentage, base.surgeon);
    return {
      consultation,
      anesthesia,
      procedure
    };
  }
);

export const selectInvoiceCalculationSelectedType = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.selectedType;

export const selectInvoiceCalculationSurgeonPrice = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.surgeonBased.surgeonPrice;

const selectSurgeonBasedPercentage = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.surgeonBased.percentage;

const selectSurgeonBasedManualPrice = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.surgeonBased.manualPrice;

const selectSurgeonBasedInvoiceTotal = createSelector(
  selectInvoiceCalculationSurgeonPrice,
  selectSurgeonBasedPercentage,
  selectSurgeonBasedManualPrice,
  (surgeonPrice, percentage, manualPrice) => {
    if (typeof manualPrice === 'number') {
      return manualPrice;
    }
    return calculateInvoiceTotal(surgeonPrice, percentage);
  }
);

const selectInsuranceBasedPercentage = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.insuranceBased.percentage;

const selectInsuranceBasedManualPrice = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.insuranceBased.manualPrice;

const selectInsuranceBasedInvoiceTotal = createSelector(
  selectInsuranceCoverage,
  selectInsuranceBasedPercentage,
  selectInsuranceBasedManualPrice,
  ({ anesthesia }, percentage, manualPrice) => {
    if (typeof manualPrice === 'number') {
      return manualPrice;
    }
    return calculateInvoiceTotal(anesthesia, percentage);
  }
);

const selectBrssBasedBase = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.brssBased.base;

const selectBrssBasedPercentage = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.brssBased.percentage;

const selectBrssBasedManualPrice = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.brssBased.manualPrice;

const selectBrssBasedInvoiceTotal = createSelector(
  selectBrssBasedBase,
  selectBrssBasedPercentage,
  selectBrssBasedManualPrice,
  (base, percentage, manualPrice) => {
    if (typeof manualPrice === 'number') {
      return manualPrice;
    }
    return calculateInvoiceTotal(base, percentage);
  }
);

export const selectAnesthesiaBasedInvoiceItems = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.anesthesiaBased;

const selectAnesthesiaBasedInvoiceTotal = (state: RootState) =>
  state.invoiceBuilder.invoiceCalculation.anesthesiaBased
    .map((proc) => proc.price)
    .filter(Boolean)
    .reduce((acc, price) => (acc as number) + (price as number), 0);

export const selectInvoiceTotalByCalculationType =
  (type: InvoiceCalculationType | undefined) => (state: RootState) => {
    if (!type) {
      return 0;
    }
    switch (type) {
      case 'insuranceBased':
        return selectInsuranceBasedInvoiceTotal(state);
      case 'brssBased':
        return selectBrssBasedInvoiceTotal(state);
      case 'surgeonBased':
        return selectSurgeonBasedInvoiceTotal(state);
      case 'anesthesiaBased':
        return selectAnesthesiaBasedInvoiceTotal(state);
      default:
        throw new Error(`Unknown calculation type: ${type}`);
    }
  };

export const selectInvoiceTotalByCurrentCalculationType = (
  state: RootState
) => {
  const type = selectInvoiceCalculationSelectedType(state);
  return selectInvoiceTotalByCalculationType(type)(state);
};

function calculateInvoiceTotal(price?: number, percentage?: number) {
  if (typeof price !== 'number') {
    return 0;
  }

  if (typeof percentage !== 'number') {
    return price;
  }

  return safeMult(price, percentage);
}

/**
 * What the patient pays out of pocket under a calculation type: the CCAM part minus coverage,
 * plus the manual fees, which are never reimbursed.
 */
export const selectPatientFeesByCalculationType =
  (type: InvoiceCalculationType | undefined) => (state: RootState) => {
    if (!type) {
      return 0;
    }
    const ccamPatientFees = selectCcamPatientFeesByCalculationType(type)(state);
    return typeof ccamPatientFees === 'number'
      ? roundToCents(ccamPatientFees + selectManualFeesTotal(state))
      : ccamPatientFees;
  };

const selectCcamPatientFeesByCalculationType =
  (type: InvoiceCalculationType) => (state: RootState) => {
    switch (type) {
      case 'insuranceBased':
        return selectInsuranceBasedPatientFees(state);
      case 'brssBased':
        return selectBrssBasedPatientFees(state);
      case 'surgeonBased':
        return selectSurgeonBasedPatientFees(state);
      case 'anesthesiaBased':
        return selectAnesthesiaBasedPatientFees(state);
      default:
        throw new Error(`Unknown calculation type: ${type}`);
    }
  };

const selectSurgeonBasedPatientFees = createSelector(
  selectInsuranceCoverage,
  selectSurgeonBasedInvoiceTotal,
  calculatePatientFees
);

const selectInsuranceBasedPatientFees = createSelector(
  selectInsuranceCoverage,
  selectInsuranceBasedInvoiceTotal,
  calculatePatientFees
);

const selectBrssBasedPatientFees = createSelector(
  selectInsuranceCoverage,
  selectBrssBasedInvoiceTotal,
  calculatePatientFees
);

const selectAnesthesiaBasedPatientFees = createSelector(
  selectInsuranceCoverage,
  selectAnesthesiaBasedInvoiceTotal,
  calculatePatientFees
);

function calculatePatientFees<T extends { anesthesia?: number }>(
  coverage?: T,
  invTotal?: number
) {
  if (typeof invTotal !== 'number') {
    return undefined;
  }
  const anesthesiaCoverage = coverage?.anesthesia ?? 0;
  const fee = invTotal - anesthesiaCoverage;
  return fee < 0 ? 0 : roundToPlaces(fee);
}

export const selectManualFees = (state: RootState) =>
  state.invoiceBuilder.manualFees;

/** Row ids only, so a list can render without re-rendering on every keystroke in a row. */
export const selectManualFeeIds = (state: RootState): number[] =>
  state.invoiceBuilder.manualFees.map((fee) => fee.id);

export const selectManualFeeById = (id: number) => (state: RootState) =>
  state.invoiceBuilder.manualFees.find((fee) => fee.id === id);

/** Manual fees that can be sent: a non-blank label and a typed amount. */
export const selectValidManualFees = createSelector(
  selectManualFees,
  (fees): ManualFee[] =>
    fees.flatMap(({ label, amount }) =>
      label.trim() && typeof amount === 'number'
        ? [{ label: label.trim(), amount }]
        : []
    )
);

/** True while some manual fee row still lacks a label or a valid amount. */
export const selectHasIncompleteManualFee = (state: RootState) =>
  selectValidManualFees(state).length !== selectManualFees(state).length;

export const selectManualFeesTotal = createSelector(
  selectValidManualFees,
  (fees) => roundToCents(fees.reduce((acc, { amount }) => acc + amount, 0))
);

export const selectHasCcamCodes = (state: RootState) =>
  selectSelectedProcedureReimbursementInfoCodes(state).length > 0;

/**
 * The CCAM part of the devis: the selected calculation's total. Without a CCAM code the
 * calculation blocks are meaningless, so a leftover value there counts for nothing.
 */
export const selectCcamTotal = (state: RootState) =>
  selectHasCcamCodes(state)
    ? roundToCents(selectInvoiceTotalByCurrentCalculationType(state) ?? 0)
    : 0;

/** The devis price sent to the backend: the CCAM part plus the manual fees. */
export const selectInvoiceTotal = (state: RootState) =>
  roundToCents(selectCcamTotal(state) + selectManualFeesTotal(state));

/**
 * What the patient pays for the whole devis: the selected calculation's out of pocket once CCAM
 * codes price it, else the manual fees alone, which no insurance covers.
 */
export const selectPatientFees = (state: RootState) =>
  selectHasCcamCodes(state)
    ? selectPatientFeesByCalculationType(
        selectInvoiceCalculationSelectedType(state)
      )(state)
    : selectManualFeesTotal(state);

/**
 * The input a calculation still needs before it can price the CCAM part: the surgeon's fee for
 * the surgeon-based one, the insurance coverage for the insurance-based one. A typed price
 * replaces the calculation, so nothing is missing then.
 */
export const selectMissingPricingInput =
  (type: InvoiceCalculationType | undefined) =>
  (state: RootState): 'surgeonPrice' | 'coverage' | undefined => {
    const { surgeonBased, insuranceBased } =
      state.invoiceBuilder.invoiceCalculation;
    if (
      type === 'surgeonBased' &&
      !surgeonBased.surgeonPrice &&
      typeof surgeonBased.manualPrice !== 'number'
    ) {
      return 'surgeonPrice';
    }
    if (
      type === 'insuranceBased' &&
      !selectInsuranceCoverage(state).anesthesia &&
      typeof insuranceBased.manualPrice !== 'number'
    ) {
      return 'coverage';
    }
    return undefined;
  };

/**
 * A devis can be issued when every manual fee row is complete and it is priced by at least one
 * row: CCAM codes need a selected calculation that has all its inputs, a manual-fees-only devis
 * needs no calculation.
 */
export const selectCanSendInvoice = (state: RootState) => {
  if (selectHasIncompleteManualFee(state)) {
    return false;
  }
  if (!selectHasCcamCodes(state)) {
    return selectValidManualFees(state).length > 0;
  }
  const selectedType = selectInvoiceCalculationSelectedType(state);
  return !!selectedType && !selectMissingPricingInput(selectedType)(state);
};

export const selectIsSurgeonBasedPriceManual = (state: RootState) =>
  typeof state.invoiceBuilder.invoiceCalculation.surgeonBased.manualPrice ===
  'number';

export const selectIsInsuranceBasedPriceManual = (state: RootState) =>
  typeof state.invoiceBuilder.invoiceCalculation.insuranceBased.manualPrice ===
  'number';

export const selectIsBrssBasedPriceManual = (state: RootState) =>
  typeof state.invoiceBuilder.invoiceCalculation.brssBased.manualPrice ===
  'number';

function safeMult(a: number | undefined, b: number | undefined) {
  return a && b && roundToPlaces(a * b);
}

export const selectSelectedModifiersForCode =
  (code: string | undefined) =>
  (state: RootState): string[] =>
    code ? state.invoiceBuilder.selectedModifiersByCode[code] ?? [] : [];

export const selectModifiedBrssForIndex =
  (
    index: number,
    modifiers: CcamModifier[],
    status: ConventionStatus | null | undefined
  ) =>
  (state: RootState): number => {
    const base =
      selectProcedureReimbursementBaseForIndex(index)(state).anesthesia ?? 0;
    const code =
      state.invoiceBuilder.procedureReimbursements[index]?.info?.code;
    if (!base || !status || !modifiers.length || !code) {
      return base;
    }
    const selected = state.invoiceBuilder.selectedModifiersByCode[code] ?? [];
    const active = modifiers.filter((m) => selected.includes(m.code));
    const sumPct = active
      .filter((m) => m.type === 'percentage')
      .reduce((acc, m) => acc + (m.valuesByStatus[status] ?? 0), 0);
    const sumFixed = active
      .filter((m) => m.type === 'fixed_eur')
      .reduce((acc, m) => acc + (m.fixedValueEur ?? 0), 0);
    return roundToPlaces(base * (1 + sumPct) + sumFixed);
  };

export const selectModifiedBrssTotal =
  (modifiers: CcamModifier[], status: ConventionStatus | null | undefined) =>
  (state: RootState): number => {
    const count = selectProcedureReimbursementInfosCount(state);
    let total = 0;
    for (let i = 0; i < count; i++) {
      total += selectModifiedBrssForIndex(i, modifiers, status)(state);
    }
    return roundToPlaces(total);
  };

export const selectAppliedModifiersByCode = (
  state: RootState
): Record<string, string[]> => state.invoiceBuilder.selectedModifiersByCode;
